import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';

/**
 * TutorialContext — single source of truth for the onboarding tour.
 *
 * Lives above the router so the tour survives page navigations between
 * dashboard / live-coaching / learn steps without unmounting. Consumers
 * (the mounted <TutorialOverlay/> and the entry-point buttons) subscribe
 * via `useTutorial()`.
 *
 * Backend contract:
 *   GET  /api/tutorial/script    → { version, completed, steps: [...] }
 *   GET  /api/tutorial/audio/:id → audio/mpeg (Bearer-authed, cached on disk)
 *   POST /api/tutorial/complete  → marks user.tutorial_completed = true
 *   POST /api/tutorial/reset     → marks user.tutorial_completed = false
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TutorialContext = createContext(null);

export const useTutorial = () => {
  const ctx = useContext(TutorialContext);
  if (!ctx) {
    throw new Error('useTutorial must be used inside <TutorialProvider>');
  }
  return ctx;
};

/**
 * Convert Blob → Object URL. Kept as a helper so callers don't leak URLs
 * (the caller revokes on step change and unmount).
 */
const blobUrl = (blob) => URL.createObjectURL(blob);

export const TutorialProvider = ({ children }) => {
  const [steps, setSteps] = useState([]);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [completed, setCompleted] = useState(true); // start truthy so we don't auto-open before /script resolves
  const [isActive, setIsActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);

  // Track the last blob URL so we can revoke it before creating a new one.
  const audioUrlRef = useRef(null);

  // Guard against the auto-launch firing twice under StrictMode's double-mount.
  const autoLaunchAttemptedRef = useRef(false);

  const authHeader = () => {
    const token = localStorage.getItem('sage_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchScript = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tutorial/script`, {
        headers: authHeader(),
      });
      setSteps(data.steps || []);
      setCompleted(!!data.completed);
      setScriptLoaded(true);
    } catch (e) {
      // Non-fatal: unauthenticated visitors just never see the tour.
      setScriptLoaded(true);
    }
  }, []);

  // One-time script fetch on mount if the user is authenticated. We watch
  // localStorage indirectly by re-checking on window `storage` events, so
  // logging in from another tab or right after this provider mounted still
  // arms the tour.
  useEffect(() => {
    if (localStorage.getItem('sage_token')) {
      fetchScript();
    }
    const onStorage = (e) => {
      if (e.key === 'sage_token' && e.newValue) {
        fetchScript();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [fetchScript]);

  // When the active step changes, fetch its audio as a blob and expose it
  // via an object URL. We fetch instead of using <audio src=...> directly
  // because the endpoint requires an Authorization header — cookies aren't
  // in play here.
  useEffect(() => {
    if (!isActive || !steps.length) {
      return undefined;
    }
    const step = steps[stepIndex];
    if (!step) return undefined;

    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/tutorial/audio/${step.id}`, {
          headers: authHeader(),
          responseType: 'blob',
        });
        if (cancelled) return;
        if (audioUrlRef.current) {
          URL.revokeObjectURL(audioUrlRef.current);
        }
        const url = blobUrl(res.data);
        audioUrlRef.current = url;
        setAudioUrl(url);
      } catch {
        // If audio fails, UI falls back to text-only silently.
        setAudioUrl(null);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isActive, stepIndex, steps]);

  // Revoke the last blob URL on unmount so we don't leak in a long session.
  useEffect(() => {
    return () => {
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
    };
  }, []);

  const startTutorial = useCallback(async () => {
    // Ensure we have the freshest script + completion flag before launching.
    if (!scriptLoaded) {
      await fetchScript();
    }
    setStepIndex(0);
    setIsActive(true);
  }, [scriptLoaded, fetchScript]);

  const closeTutorial = useCallback(
    async ({ markComplete = true } = {}) => {
      setIsActive(false);
      setStepIndex(0);
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
      setAudioUrl(null);
      if (markComplete) {
        setCompleted(true);
        try {
          await axios.post(
            `${API}/tutorial/complete`,
            {},
            { headers: authHeader() },
          );
        } catch (err) {
          // Non-fatal — the client-side flag is enough for this session.
          if (process.env.NODE_ENV !== 'production') console.debug('tutorial/complete failed:', err);
        }
      }
    },
    [],
  );

  const nextStep = useCallback(() => {
    setStepIndex((i) => {
      if (i + 1 >= steps.length) {
        // Last step advanced → treat as completion (dismiss + mark done).
        closeTutorial({ markComplete: true });
        return i;
      }
      return i + 1;
    });
  }, [steps.length, closeTutorial]);

  const prevStep = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const resetTutorial = useCallback(async () => {
    // Called by the "Take the tour" entry point. We flip the server-side
    // flag AND immediately start — both are needed because the flag alone
    // wouldn't auto-open on this same session (auto-launch only runs once
    // per mount).
    try {
      await axios.post(
        `${API}/tutorial/reset`,
        {},
        { headers: authHeader() },
      );
    } catch (err) {
      // Non-fatal.
      if (process.env.NODE_ENV !== 'production') console.debug('tutorial/reset failed:', err);
    }
    setCompleted(false);
    autoLaunchAttemptedRef.current = false;
    setStepIndex(0);
    setIsActive(true);
  }, []);

  const requestAutoLaunch = useCallback(() => {
    // Called by Dashboard on mount. Fires the tour exactly once per app
    // session for a user who hasn't completed it yet.
    if (autoLaunchAttemptedRef.current) return;
    if (!scriptLoaded) return;
    if (completed) return;
    if (!steps.length) return;
    autoLaunchAttemptedRef.current = true;
    setStepIndex(0);
    setIsActive(true);
  }, [scriptLoaded, completed, steps.length]);

  const value = {
    steps,
    scriptLoaded,
    completed,
    isActive,
    stepIndex,
    audioUrl,
    startTutorial,
    closeTutorial,
    nextStep,
    prevStep,
    resetTutorial,
    requestAutoLaunch,
  };

  return (
    <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>
  );
};

export default TutorialContext;
